// @ts-check
import ZeroDataGrid from '~/components/Shared/ZeroDataGrid/ZeroDataGrid';
import { useDataGridSetup } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import useOrganizationId from '~/hooks/useOrganizationId';
import useZeroSelector from '~/hooks/useZeroSelector';
import { objectIsEmpty, setDefaultSearchParam, setGridLoading } from '~/other/Helper';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTemplateDataGridContext } from '~/components/Forms/TemplateDataGrid/TemplateDataGrid.context';
import { TemplateDataGridModals } from '~/components/Forms/TemplateDataGrid/TemplateDataGrid.modals';
import { EventNames, getColumnDefs, getSortByColumnName, loadTemplates } from '~/components/Forms/TemplateDataGrid/TemplateDataGrid.utils';

const TEMPLATES_PER_PAGE = 50;

function useTemplateDataGridQuery() {
    const formsQuery = useZeroSelector(state => state.forms.query);
    const [query, setQuery] = useState({ formsQuery });

    useEffect(() => {
        setQuery({ formsQuery });
    }, [formsQuery]);

    return query;
}

/**
 * @param {{
 *  pageType: string,
 *  showCreateModal: boolean,
 *  onCloseCreateModal: () => void;
 * }} props
 */
export default function TemplateDataGrid({ pageType, showCreateModal, onCloseCreateModal }) {
    const location = useLocation();
    const { gridRef, settings, setSettings, rowCount } = useTemplateDataGridContext();
    const query = useTemplateDataGridQuery();
    const categories = useZeroSelector((state) => state.forms?.filterOptions?.categories ?? []);

    const colDefs = useMemo(() => getColumnDefs(pageType, categories), [pageType, categories]);

    const getRows = useCallback(async (/** @type {AgGrid.IGetRowsParams} */params) => {
        const perPage = TEMPLATES_PER_PAGE;
        const currentPage = Math.floor((params.startRow ?? 0) / perPage) + 1;

        const { formsQuery } = query;

        const queryParams = new URLSearchParams(formsQuery);
        setDefaultSearchParam(queryParams, 'team_uuid', 'my_teams');
        queryParams.set('form_types', pageType);
        queryParams.set('include_fields', 'false');
        queryParams.set('exclude_restricted', 'true');
        queryParams.set('paginate', 'true');
        queryParams.set('page', String(currentPage));
        queryParams.set('per_page', String(perPage));

        if (!objectIsEmpty(params.filterModel)) {
            queryParams.set('ag_filter', JSON.stringify(params.filterModel));
        }

        params.sortModel.forEach(sortModel => {
            let col = getSortByColumnName(sortModel.colId);
            if (sortModel.sort === 'desc') {
                col = `-${col}`;
            }
            queryParams.append('sort_by', col);
        });

        const gridApi = gridRef.current?.api;
        if (rowCount === null) {
            setGridLoading(gridApi, true);
        }
        
        const { forms, totalForms } = await loadTemplates(queryParams);

        params.successCallback(forms, totalForms);
        if (rowCount === null) {
            setGridLoading(gridApi, false);
        }
    }, [rowCount, query, pageType]);

    const clearTemplateCache = useCallback(() => {
        loadTemplates.cache.clear();
    }, [loadTemplates]);

    const { onGridReady, onStateUpdated } = useDataGridSetup({
        eventNames: EventNames,
        autoLoadData: true,
        gridRef,
        colDefs,
        getRows,
        setSettings,
        onReset: clearTemplateCache,
        onRefresh: clearTemplateCache,
    });
    
    useEffect(() => {
        // Clear memoization when component is unmounted
        return () => {
            clearTemplateCache();
        }
    }, []);

    return (
        <>
            <ZeroDataGrid
                gridRef={gridRef}
                columnDefs={colDefs}
                defaultColDef={{
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
                initialState={location.state?.gridState ?? settings}
                onGridReady={onGridReady}
                onStateUpdated={onStateUpdated}
                cacheBlockSize={TEMPLATES_PER_PAGE}
                cacheOverflowSize={2}
                maxConcurrentDatasourceRequests={2}
                infiniteInitialRowCount={rowCount ?? TEMPLATES_PER_PAGE}
                pagination={true}
                paginationPageSizeSelector={false}
                paginationPageSize={rowCount ?? TEMPLATES_PER_PAGE}
                tooltipShowDelay={500}
                tooltipShowMode="standard"
                suppressCellFocus
            />
            <TemplateDataGridModals 
                showCreateModal={showCreateModal} 
                onCloseCreateModal={onCloseCreateModal}
                pageType={pageType}
            />
        </>
    );
}
